import { decode } from "quoted-printable";
import { useMemo } from "react";

// ─── Input types ─────────────────────────────────────────────────────────────
// Accepts any email-like object — EmailBody, UnassignedContent, lightweight
// email from timeline elements, or raw fields. The hook normalises them all.

export interface EmailInput {
    subject?: string | null;
    from?: string | null;
    to?: string | null;
    // body text — components use either `content` or `body`
    content?: string | null;
    body?: string | null;
    summary?: string | null;
    // date — components use either `createdAt` (string) or `created` (Date)
    createdAt?: string | null;
    created?: Date | string | null;
    // classification
    type?: string | null;
    contentClassification?: string | null;
    // assets
    attachments?: string[] | null;
    externalLinks?: string[] | null;
    folderId?: string | null;
    id?: string | null;
}

// ─── Sanitisation rules ──────────────────────────────────────────────────────

export enum EmailContentRule {
    /** Decode quoted-printable encoding */
    DECODE_QP = "DECODE_QP",
    /** Strip soft line-breaks produced by QP encoding (=\n) */
    STRIP_SOFT_BREAKS = "STRIP_SOFT_BREAKS",
    /** Normalise \r\n → \n */
    NORMALISE_NEWLINES = "NORMALISE_NEWLINES",
    /** Collapse 3+ consecutive blank lines into 2 */
    COLLAPSE_BLANK_LINES = "COLLAPSE_BLANK_LINES",
    /** Trim leading/trailing whitespace */
    TRIM = "TRIM",
    /** Decode HTML entities: &amp; → &, &lt; → <, &gt; → >, &nbsp; → space, &quot; → ", &#NNN; → char */
    DECODE_HTML_ENTITIES = "DECODE_HTML_ENTITIES",
    /** Strip residual HTML tags from HTML-to-text fallback */
    STRIP_HTML_TAGS = "STRIP_HTML_TAGS",
    /** Remove Unicode replacement characters (�) from bad charset decoding */
    STRIP_REPLACEMENT_CHARS = "STRIP_REPLACEMENT_CHARS",
    /** Remove non-printable control characters (except \n \t) */
    STRIP_CONTROL_CHARS = "STRIP_CONTROL_CHARS",
    /** Clean up stale =XX hex sequences that survived a failed QP decode */
    STRIP_STALE_QP = "STRIP_STALE_QP",
    /** Normalise whitespace: tabs → spaces, collapse multiple spaces into one (per line) */
    NORMALISE_WHITESPACE = "NORMALISE_WHITESPACE",
    /** Fix broken lists: lone "-" or "•" lines between content → merge as "- content" bullet items */
    FIX_BROKEN_LISTS = "FIX_BROKEN_LISTS",
    /** Rejoin angle-bracket URLs that were line-wrapped by email clients */
    REJOIN_WRAPPED_LINKS = "REJOIN_WRAPPED_LINKS",
}

/** Default pipeline — order matters. */
const ALL_RULES: EmailContentRule[] = [
    EmailContentRule.DECODE_QP,
    EmailContentRule.NORMALISE_NEWLINES,
    EmailContentRule.STRIP_SOFT_BREAKS,
    EmailContentRule.STRIP_STALE_QP,
    EmailContentRule.REJOIN_WRAPPED_LINKS,
    EmailContentRule.STRIP_HTML_TAGS,
    EmailContentRule.DECODE_HTML_ENTITIES,
    EmailContentRule.STRIP_REPLACEMENT_CHARS,
    EmailContentRule.STRIP_CONTROL_CHARS,
    EmailContentRule.NORMALISE_WHITESPACE,
    EmailContentRule.FIX_BROKEN_LISTS,
    EmailContentRule.COLLAPSE_BLANK_LINES,
    EmailContentRule.TRIM,
];

// Named HTML entities we care about (covers >99% of email content)
const HTML_ENTITIES: Record<string, string> = {
    amp: "&", lt: "<", gt: ">", nbsp: " ", quot: '"',
    apos: "'", ndash: "–", mdash: "—", lsquo: "\u2018",
    rsquo: "\u2019", ldquo: "\u201C", rdquo: "\u201D",
    bull: "\u2022", hellip: "\u2026", copy: "\u00A9",
    reg: "\u00AE", trade: "\u2122", deg: "\u00B0",
};

function decodeHtmlEntities(text: string): string {
    return text
        // Named entities: &amp; &nbsp; etc.
        .replace(/&([a-zA-Z]+);/g, (match, name) => HTML_ENTITIES[name.toLowerCase()] ?? match)
        // Decimal numeric entities: &#160;
        .replace(/&#(\d+);/g, (_, code) => {
            const n = parseInt(code, 10);
            return n > 0 && n < 0x10FFFF ? String.fromCodePoint(n) : "";
        })
        // Hex numeric entities: &#x00A0;
        .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => {
            const n = parseInt(hex, 16);
            return n > 0 && n < 0x10FFFF ? String.fromCodePoint(n) : "";
        });
}

function applyRule(text: string, rule: EmailContentRule): string {
    switch (rule) {
        case EmailContentRule.DECODE_QP:
            try { return decode(text).toString(); }
            catch { return text; }

        case EmailContentRule.NORMALISE_NEWLINES:
            return text.replace(/\r\n/g, "\n");

        case EmailContentRule.STRIP_SOFT_BREAKS:
            return text.replace(/=\n/g, "");

        case EmailContentRule.COLLAPSE_BLANK_LINES:
            return text.replace(/\n{3,}/g, "\n\n");

        case EmailContentRule.TRIM:
            return text.trim();

        case EmailContentRule.DECODE_HTML_ENTITIES:
            return decodeHtmlEntities(text);

        case EmailContentRule.STRIP_HTML_TAGS:
            // Remove HTML tags but preserve the inner text.
            // Adds a newline for block-level tags to avoid text running together.
            // IMPORTANT: preserve angle-bracket URLs like <https://...> — these
            // are Gmail's plain-text link syntax, not HTML tags.
            return text
                .replace(/<br\s*\/?>/gi, "\n")
                .replace(/<\/?(p|div|tr|li|h[1-6]|blockquote|section|article|header|footer|pre|hr)[^>]*>/gi, "\n")
                .replace(/<[^>]+>/g, (match) => {
                    if (/^<\s*(?:https?:\/\/|www\.)/i.test(match)) return match;
                    return "";
                });

        case EmailContentRule.STRIP_REPLACEMENT_CHARS:
            return text.replace(/\uFFFD/g, "");

        case EmailContentRule.STRIP_CONTROL_CHARS:
            // Remove C0 controls except \n (0x0A) and \t (0x09), plus C1 controls (0x80-0x9F)
            // eslint-disable-next-line no-control-regex
            return text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F\x80-\x9F]/g, "");

        case EmailContentRule.STRIP_STALE_QP:
            // Leftover =XX hex pairs from a failed QP decode (e.g. =C3=A9 for é)
            // Attempt to decode them as UTF-8 byte sequences
            return text.replace(/(?:=[0-9A-Fa-f]{2})+/g, (match) => {
                try {
                    const bytes = match.split("=").filter(Boolean).map(h => parseInt(h, 16));
                    return new TextDecoder("utf-8", { fatal: true }).decode(new Uint8Array(bytes));
                } catch {
                    return match; // leave as-is if not valid UTF-8
                }
            });

        case EmailContentRule.REJOIN_WRAPPED_LINKS:
            // Email clients line-wrap long angle-bracket URLs across multiple
            // lines, e.g.:
            //   Label\n<\nview.example.com/very-long\n-path-continues\n>
            //   (*TechCrunch*\n<\nhttps://techcrunch.com/...\n>)
            // Rejoin the URL onto a single line so the link parser can match it.
            // Also pulls any preceding label text onto the same line.
            return text.replace(
                /(?:^|(\n))([^\n<>]*?)\n?<[\n ]*((?:https?:\/\/|www\.)?[a-z0-9][^\s>]*(?:\n[^\s>]+)*)[\n ]*>/gim,
                (_match, leadingNl: string | undefined, label: string, urlParts: string) => {
                    const url = urlParts.replace(/\n/g, "");
                    const prefix = leadingNl ?? "";
                    const trimmedLabel = label.trim();
                    if (trimmedLabel) {
                        return `${prefix}${trimmedLabel} <${url}>`;
                    }
                    return `${prefix}<${url}>`;
                },
            );

        case EmailContentRule.NORMALISE_WHITESPACE:
            // Tabs → spaces, collapse runs of spaces (but not newlines)
            return text.replace(/\t/g, " ").replace(/ {2,}/g, " ");

        case EmailContentRule.FIX_BROKEN_LISTS:
            // Pattern: lone "-" or "•" on its own line (possibly indented, possibly
            // surrounded by blank lines) acting as a broken bullet separator.
            // The content line after may also be indented. Strip indentation and
            // merge into "- content".
            return text.replace(/\n(?:[ ]*\n)*[ ]*[-•][ ]*\n(?:[ ]*\n)*[ ]*/g, "\n- ");

        default:
            return text;
    }
}

// ─── Output ──────────────────────────────────────────────────────────────────

export interface EmailPreview {
    id: string | null;
    subject: string | null;
    from: string | null;
    fromName: string | null;
    to: string | null;
    date: string | null;
    summary: string | null;
    type: string | null;
    contentClassification: string | null;
    attachments: string[];
    externalLinks: string[];
    folderId: string | null;
    rawContent: string | null;
    /** Returns sanitised email body content. Pass rules to customise; defaults to all rules. */
    sanitizedContent: (rules?: EmailContentRule[]) => string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractFromName(from: string | null | undefined): string | null {
    if (!from) return null;
    // "John Doe <john@doe.com>" → "John Doe"
    const match = from.match(/^(.+?)\s*<[^>]+>$/);
    return match ? match[1].trim() : from;
}

function resolveDate(input: EmailInput): string | null {
    if (input.createdAt) return input.createdAt;
    if (input.created) {
        if (input.created instanceof Date) return input.created.toISOString();
        return input.created;
    }
    return null;
}

function resolveContent(input: EmailInput | null): string | null {
    if (!input) return null;
    return input.content ?? input.body ?? null;
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export default function useEmailPreview(input: EmailInput | null | undefined): EmailPreview {
    const raw = resolveContent(input ?? null);

    const sanitizedContent = useMemo(() => {
        const cache = new Map<string, string | null>();

        return (rules?: EmailContentRule[]): string | null => {
            if (raw == null) return null;
            const effectiveRules = rules ?? ALL_RULES;
            const key = effectiveRules.join(",");
            if (cache.has(key)) return cache.get(key)!;
            let result = raw;
            for (const rule of effectiveRules) {
                result = applyRule(result, rule);
            }
            cache.set(key, result);
            return result;
        };
    }, [raw]);

    return useMemo<EmailPreview>(() => ({
        id: input?.id ?? null,
        subject: input?.subject ?? null,
        from: input?.from ?? null,
        fromName: extractFromName(input?.from),
        to: input?.to ?? null,
        date: resolveDate(input ?? ({} as EmailInput)),
        summary: input?.summary ?? null,
        type: input?.type ?? null,
        contentClassification: input?.contentClassification ?? null,
        attachments: input?.attachments ?? [],
        externalLinks: input?.externalLinks ?? [],
        folderId: input?.folderId ?? null,
        rawContent: raw,
        sanitizedContent,
    }), [input, raw, sanitizedContent]);
}

// Re-export for convenience — components can also call this outside React
export function sanitizeEmailContent(
    content: string | null | undefined,
    rules: EmailContentRule[] = ALL_RULES,
): string | null {
    if (content == null) return null;
    let result = content;
    for (const rule of rules) {
        result = applyRule(result, rule);
    }
    return result;
}
